import React, { FC, useState } from 'react';
import Comments from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog/Comments';
import Logs from '../../../../../../clients/sidebar/views/SidebarPanel/Detailbar/CommentsAndLog/Logs';
import useComments from 'appJS/hooks/useComments';
import { useLogs } from 'appJS/hooks/useLogs';
import { MentionableTask } from '../../../../../../clients/sidebar/views/CreateTask/create_task/types';
import { useTaskMentionSearch } from './useTaskMentionSearch';
import { DashboardTask } from './queries';
import {
  DashboardProjectAccess,
  DashboardProjectContext
} from './projectContextQueries';

type Props = {
  task: DashboardTask;
  bugherdUrl: string;
  container: HTMLElement;
  currentUserId: string;
  projectContext: DashboardProjectContext | null;
  accessTo: DashboardProjectAccess | null;
  mentionableTasks: MentionableTask[];
  onOpenTask?: (taskId: number) => void;
};

export const DrawerComments: FC<Props> = ({
  task,
  bugherdUrl,
  container,
  currentUserId,
  projectContext,
  accessTo,
  mentionableTasks,
  onOpenTask
}) => {
  const taskId = Number(task.id);
  const projectId = Number(task.project.id);
  const {
    createComment,
    deleteComment,
    updateComment,
    comments,
    loading: commentsLoading
  } = useComments({
    taskId,
    projectId,
    bugherdUrl,
    currentUser: { id: currentUserId }
  });
  const [page, setPage] = useState<number>(1);
  const { searchMentionableTasks } = useTaskMentionSearch({
    projectId: task.project.id,
    task,
    bugherdUrl
  });

  if (!accessTo) return null;

  const users = (projectContext?.users || []).map(user => ({
    ...user,
    id: Number(user.id),
    displayName: user.name
  }));
  const usersNotOnProject = (projectContext?.usersNotOnProject || []).map(
    user => ({
      ...user,
      id: Number(user.id),
      displayName: user.name
    })
  );
  const owner = projectContext?.owner
    ? { ...projectContext.owner, id: Number(projectContext.owner.id) }
    : { id: 0, name: '', email: '' };

  return (
    <Comments
      container={container}
      commentsLoading={commentsLoading}
      comments={comments as any}
      currentUserId={Number(currentUserId)}
      users={users as any}
      usersNotOnProject={usersNotOnProject as any}
      taskId={taskId}
      canEditComment={accessTo.taskEditComment}
      canViewPrivateComments={accessTo.viewPrivateComments}
      canCreatePrivateComments={accessTo.createPrivateComments}
      canViewCommitComment={accessTo.viewCommitComment}
      billingRights={accessTo.billing}
      owner={owner as any}
      bugherdUrl={bugherdUrl}
      organizationId={Number(task.project.organizationId)}
      guestVisible={!!task.guestVisible}
      createComment={createComment}
      deleteComment={deleteComment}
      updateComment={updateComment}
      projectId={projectId}
      apiDomain={bugherdUrl}
      assignGuestsAllowed={accessTo.assignGuestsAllowed}
      assignGuests={!!projectContext?.assignGuests}
      assignees={[]}
      mentionableTasks={mentionableTasks}
      searchMentionableTasks={searchMentionableTasks}
      setActiveTask={onOpenTask || (() => {})}
      isAdminView
      page={page}
      setPage={setPage}
    />
  );
};

export const DrawerActivityLog: FC<{
  task: DashboardTask;
  bugherdUrl: string;
  accessTo: DashboardProjectAccess | null;
}> = ({ task, bugherdUrl, accessTo }) => {
  const taskId = Number(task.id);
  const projectId = Number(task.project.id);
  const { logs, loading: logsLoading } = useLogs({
    taskId,
    projectId,
    bugherdUrl
  });

  if (!accessTo) return null;

  return (
    <Logs
      logs={logs}
      isAdminView
      loading={logsLoading}
      canViewDescriptionChanges={accessTo.viewPrivateComments}
    />
  );
};
